import { FC, useState, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { Panel, Search, Select, Table, Column } from "@appkit4/react-components";
import { SelectValue } from "@appkit4/react-components/esm/combobox/Combobox";
import { ListUser, UserType } from "routes/analysis/types/analysis";
import { getDate, parseLockStatus, searchFilter, selectFilter } from "services/common";
import { LoaderWrapper } from "components/common/helpers";
import { ActiveFilters } from "components/layout/filters/filters";
import ToolTip from "components/common/tooltip";
import Paginate from "components/common/paginate";
import { FetchSameRolesProfiles } from "../queries/user";

interface SameRolesProfilesProps {
  userData?: {
    userId?: string;
  };
}

const SameRolesProfiles: FC<SameRolesProfilesProps> = ({ userData }) => {
  const { analysisId, userId } = useParams();
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [offset, setOffset] = useState(25);
  const [userTypeFilter, setUserTypeFilter] = useState<SelectValue | undefined>([]);
  const [userGroupFilter, setUserGroupFilter] = useState<SelectValue | undefined>([]);

  const { data: users, isPending, error } = FetchSameRolesProfiles(analysisId, userId);

  const filteredData = useMemo(() => {
    return (
      users?.filter(f => searchFilter(f, ["name", "userName"], search))
        .filter(f => selectFilter(f, "type", userTypeFilter))
        .filter(f => selectFilter(f, "userGroup", userGroupFilter)) || []
    );
  }, [search, users, userTypeFilter, userGroupFilter]);

  // Pagination: slice filteredData for current page
  const pagedData = useMemo(() => {
    const start = (currentPage - 1) * offset;
    return filteredData.slice(start, start + offset);
  }, [filteredData, currentPage, offset]);
  return <LoaderWrapper loading={[isPending]} errors={[error]}>
    <Panel>
      <div className="flex items-center gap-4 mt-4">
        <div>
          <Search
            searchType={"secondary"}
            value={search}
            onChange={(value: string) => {
              setSearch(value);
            }}
            className="list-filter"
          />
        </div>
        <div>
          <Select
            placeholder="User type"
            data={Array.from(new Set(users?.map(m => m.type)))
              .map(m => ({ value: m, label: UserType[m].name, descValue: filteredData.filter(f => f.type === m).length }))
            }
            multiple={true}
            value={userTypeFilter}
            suffixTemplate={item => {
              return (item.descValue > 0 && <span>{item.descValue} items</span>)
            }}
            onSelect={(v) => {
              setUserTypeFilter(v);
            }}
            className="list-filter"
          />
        </div>
        <div>
          <Select
            placeholder="User group"
            data={Array.from(new Set(users?.map(m => m.userGroup))).map(m => ({ value: m, label: m, descValue: filteredData.filter(f => f.userGroup === m).length })) || []}
            searchable={true}
            multiple={true}
            value={userGroupFilter}
            suffixTemplate={item => {
              return (item.descValue > 0 && <span>{item.descValue} items</span>)
            }}
            onSelect={(v) => {
              setUserGroupFilter(v);
            }}
            className="list-filter"
          />
        </div>
      </div>
      <ActiveFilters rows={filteredData.length} />
      <Table
        originalData={pagedData}
        hasTitle
        striped
        pageSize={offset}
        currentPage={currentPage}
      >
        <Column field="userName" sortKey="userName" renderCell={(data: ListUser) => (
          <Link to={`/analysis/${analysisId}/user/${data.userId}`} reloadDocument>
            {data.userName}
          </Link>
        )}>
          Username
        </Column>
        <Column field="name" sortKey="name">
          Name
        </Column>
        <Column field="type" sortKey="type" renderCell={(data: ListUser) => (
          <ToolTip content={UserType[data.type].name} position="bottom">
            <span
              className={`Appkit4-icon ${UserType[data.type].icon}`}
            ></span>
          </ToolTip>
        )}>
          Type
        </Column>
        <Column
          field="lockStatus"
          sortKey="lockStatus"
          renderCell={(data: ListUser) => data.lockStatus > 0 && (
            <span className="table-cell">
              <ToolTip content={parseLockStatus(data["lockStatus"] || 0) || ""} position="bottom">
                <span className="Appkit4-icon icon-lockclosed-locked-fill"></span>
              </ToolTip>
            </span>
          )}
        >
          Locked
        </Column>
        <Column field="userGroup" sortKey="userGroup">
          Group
        </Column>
        <Column field="validity" sortKey="validity" renderCell={(data: ListUser) => data.validity.split(" - ").map(m => getDate(m.split(" ")[0])).join(" - ")}>
          Validity
        </Column>
        <Column field="hasMatches" sortKey="hasMatches" renderCell={(data: ListUser) => data.hasMatches && (<span className="Appkit4-icon icon-check-hook-outline"></span>)}>
          Exact match
        </Column>
      </Table>
      <Paginate
        getTotalPages={Math.ceil(filteredData.length / offset) || 1}
        currentPage={currentPage}
        pageOffset={offset}
        setCurrentPage={setCurrentPage}
        setPageOffset={setOffset}
      />
    </Panel>
  </LoaderWrapper>
}

export default SameRolesProfiles;
